// Homepage bracket widget — thin wrapper around ScheduleBracket that
// fits inside the SwipeableStandings section slot. When a featured item
// is a single-elimination tournament, this replaces the standings table
// so fans see the actual bracket tree at a glance (with horizontal
// scroll for full width) instead of a rankings/points table that
// doesn't map to elimination formats.
//
// Reuses the /public/tournaments/:id endpoint that the /schedule page
// already hits — no new API surface, guaranteed sync between what
// shows up here and what's on the full schedule page.

import { Link } from 'react-router-dom'
import { useCompetitionBracket } from '../../../core/pages/home/useCompetitionBracket'
import ScheduleBracket from '@/components/ScheduleBracket'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'

interface Props {
  tournamentId: number
  fallback?: React.ReactNode // rendered if the fetch fails or returns no games
}

export default function BracketWidget({ tournamentId, fallback }: Props) {
  // The fetch lives in core (useCompetitionBracket): the same effect, deps and
  // cancellation BracketWidget had, so the request fires exactly as before.
  const { data, loading, error } = useCompetitionBracket(tournamentId)
  const m = useT(siteClassicMessages).schedule

  if (loading) {
    return (
      <div className="bg-bg-primary/12 border border-secondary/30 p-4">
        <div className="animate-pulse space-y-2">
          <div className="h-4 bg-white/10 rounded w-1/2" />
          <div className="h-16 bg-white/10 rounded" />
          <div className="h-16 bg-white/10 rounded" />
        </div>
      </div>
    )
  }

  // Any failure to render the bracket cleanly falls back to whatever the
  // parent supplied (usually the legacy standings table). This means a
  // busted endpoint or a tournament with no games never blocks the
  // homepage — worst case, fans see the old view.
  if (error || !data || !data.games || data.games.length === 0) {
    return <>{fallback}</>
  }

  return (
    <div className="relative">
      {/* The bracket. ScheduleBracket already handles horizontal scroll
          on its own container (overflow-x-auto), the play-in split
          layout, connector-line suppression in manual mode, and slot
          labels for TBD slots. We don't need to reimplement any of it. */}
      <ScheduleBracket
        games={data.games}
        teams={data.teams}
        autoAdvance={data.tournament.auto_advance !== false}
        compact
      />

      {/* View full bracket CTA — matches the "VIEW FULL BRACKET" copy
          the ScheduleBracket cards use internally so the CTA reads
          consistently across surfaces. */}
      <div className="mt-3 text-center">
        <Link
          to={`/schedule/${tournamentId}`}
          className="inline-block text-xs uppercase tracking-widest font-bold text-primary hover:text-primary/80"
        >
          {m.viewFullBracket}
        </Link>
      </div>
    </div>
  )
}
